import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useLocation } from "react-router-dom";
import { PiWarningCircleBold } from "react-icons/pi";

import Cookies from 'js-cookie';
import { getUserSession } from "./lib/Session";

const LegalAcknowledgmentGate = () => {
  const location = useLocation();
  const isMountedRef = useRef(true);
  const [session, setSession] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);

  const refreshSession = useCallback(async () => {
    setRefreshing(true);
    try {
      const result = await getUserSession();
      if (!isMountedRef.current) {
        return;
      }
      if (result.success) {
        setSession(result);
      } else {
        setSession(null);
      }
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }
      setSession(null);
    } finally {
      if (!isMountedRef.current) {
        return;
      }
      setInitializing(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  useEffect(() => {
    const onSignIn = () => {
      refreshSession();
    };
    const onSignOut = () => {
      refreshSession();
    };
    window.addEventListener('app:auth:signin', onSignIn);
    window.addEventListener('app:auth:signout', onSignOut);
    return () => {
      window.removeEventListener('app:auth:signin', onSignIn);
      window.removeEventListener('app:auth:signout', onSignOut);
    };
  }, [refreshSession]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const path = (location.pathname || "").toLowerCase();
  const bypassGate =
    path.startsWith("/database") ||
    path.startsWith("/privacy-policy") ||
    path.startsWith("/terms-and-conditions");
  const latestTermsVersion = session?.latest_terms_version || session?.latestTermsVersion;
  const latestPrivacyVersion = session?.latest_privacy_version || session?.latestPrivacyVersion;

  const termsOutdated = latestTermsVersion
    ? session?.terms_version_acknowledged !== latestTermsVersion
    : !session?.terms_version_acknowledged;
  const privacyOutdated = latestPrivacyVersion
    ? session?.privacy_version_acknowledged !== latestPrivacyVersion
    : !session?.privacy_version_acknowledged;

  const missingAcknowledgment = !!session && (termsOutdated || privacyOutdated);

  useEffect(() => {
    if (!missingAcknowledgment) {
      setAcceptedTerms(false);
      setAcceptedPrivacy(false);
    }
  }, [missingAcknowledgment]);

  const hasAcceptedBoth = acceptedTerms && acceptedPrivacy;
  const shouldDisplay = !initializing && !bypassGate && missingAcknowledgment;

  const primaryBg = "bg-gray-800";

//   if (!shouldDisplay) {
//   const primaryBg = "bg-blue-600"; // Updated to align with signup flow
//   }

  const submitAcknowledgment = useCallback(async () => {
    if (!acceptedTerms || !acceptedPrivacy) return;

    setRefreshing(true);
    try {
      const csrftoken = Cookies.get('csrftoken') || '';
      const url = '/api/auth/acknowledge-legal/';
      console.log('POST', url, { acceptedTerms, acceptedPrivacy });

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrftoken,
        },
        body: JSON.stringify({ acceptedTerms, acceptedPrivacy }),
      });

      if (response.ok) {
        await refreshSession();
        return;
      }

      let errText = 'Failed to save acknowledgment. Please try again.';
      try {
        const data = await response.json();
        if (data && data.error) errText = data.error;
      } catch (e) {}
      alert(errText);
    } catch (error) {
      console.error('Error submitting acknowledgment:', error);
      alert('An error occurred. Please try again.');
    } finally {
      if (isMountedRef.current) setRefreshing(false);
    }
  }, [acceptedTerms, acceptedPrivacy, refreshSession]);

  return (
    <Transition.Root show={shouldDisplay} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => null} static>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/80" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <Dialog.Panel className={`w-full max-w-2xl rounded-3xl ${primaryBg} px-8 py-10 text-left shadow-2xl text-white`}>
              <div className="flex flex-col gap-8">
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10">
                      <PiWarningCircleBold className="h-8 w-8 text-blue-600" aria-hidden="true" />
                    </div>
                    <div>
                      <Dialog.Title as="h2" className="text-3xl font-semibold">
                        Legal acknowledgments needed
                      </Dialog.Title>
                      <p className="text-sm text-white/70">
                        Please review and acknowledge our latest Terms & Conditions and Privacy Policy to continue.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 rounded-2xl bg-white/5 p-6">
                  <label className="flex items-start gap-3 text-base">
                    <input
                      type="checkbox"
                      checked={acceptedPrivacy}
                      onChange={(event) => setAcceptedPrivacy(event.target.checked)}
                      className="mt-1 h-5 w-5 cursor-pointer rounded border-white/30 bg-white/10 text-blue-600 focus:ring-blue-600"
                    />
                    <span>
                      I read and agree to the
                      {" "}
                      <button
                        type="button"
                        className="font-semibold text-blue-600 underline"
                        onClick={() => window.open("/privacy-policy", "_blank")}
                      >
                        Privacy Policy
                      </button>
                      .
                    </span>
                  </label>

                  <label className="flex items-start gap-3 text-base">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(event) => setAcceptedTerms(event.target.checked)}
                      className="mt-1 h-5 w-5 cursor-pointer rounded border-white/30 bg-white/10 text-blue-600 focus:ring-blue-600"
                    />
                    <span>
                      I read and agree to the
                      {" "}
                      <button
                        type="button"
                        className="font-semibold text-blue-600 underline"
                        onClick={() => window.open("/terms-and-conditions", "_blank")}
                      >
                        Terms & Conditions
                      </button>
                      .
                    </span>
                  </label>
                </div>

                <div className="flex flex-col gap-3 text-sm text-white/70">
                  <p>
                    You’ll regain access as soon as both documents are reviewed.
                  </p>
                </div>

                <div className="flex justify-center gap-4">
                  <button
                    onClick={submitAcknowledgment}
                    disabled={!acceptedTerms || !acceptedPrivacy || refreshing}
                    className={`inline-flex w-40 justify-center rounded-xl border border-transparent px-5 py-3 text-base font-semibold text-white shadow-sm transition focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 sm:w-auto ${
                      hasAcceptedBoth && !refreshing
                        ? "bg-blue-600 hover:bg-blue-700"
                        : "bg-white/10 text-white/50 cursor-not-allowed"
                    }`}
                  >
                    {refreshing ? "Syncing status..." : "Submit"}
                  </button>
                 
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default LegalAcknowledgmentGate;
